import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Form, Table, Card, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import currencyApi from '../apiProvider/currencyapi';
import productApi from '../apiProvider/product';
import ShowNotifications from '../utils/notification';
import materialApi from '../apiProvider/materialapi';
import { IMAGE_BASE_URL } from '../network/apiClient';

const MATERIAL_ICONS = [
    "solar:atom-bold-duotone", "solar:box-bold-duotone", "solar:compass-bold-duotone",
    "solar:droplet-bold-duotone", "solar:energy-bold-duotone", "solar:fire-bold-duotone",
    "solar:layers-bold-duotone", "solar:leaf-bold-duotone", "solar:mask-h-bold-duotone",
    "solar:ruler-bold-duotone", "solar:settings-bold-duotone", "solar:shield-bold-duotone",
    "solar:stars-bold-duotone", "solar:tuning-bold-duotone", "solar:water-bold-duotone"
];

export default function MaterialPricingLayer() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [currencies, setCurrencies] = useState([]);
    const [activeMaterials, setActiveMaterials] = useState([]);
    const [availableMaterials, setAvailableMaterials] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({}); // { mId: { rowId: { field: msg } } }
    const [materialPricing, setMaterialPricing] = useState({}); // { mId: { rowId: { selected, stock, sku, pricing } } }
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState(null);

    useEffect(() => {
        if (id) {
            fetchInitialData();
        }
    }, [id]);

    const urlToFile = async (url, name, mime) => {
        try {
            const res = await fetch(url);
            if (!res.ok) return null;
            const blob = await res.blob();
            return new File([blob], name, { type: mime });
        } catch (e) {
            console.error("Image conversion failed:", e);
            return null;
        }
    };

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const [prodRes, currRes, matRes] = await Promise.all([
                productApi.productDetails(id),
                currencyApi.getCurrencies({ limit: 100, status: true }),
                materialApi.materialList({ limit: 100, status: true })
            ]);

            if (matRes.status) {
                const mats = (matRes.response?.data || []).map((m, i) => ({
                    id: m._id, // USE _ID INSTEAD OF NAME
                    label: m.name,
                    icon: MATERIAL_ICONS[i % MATERIAL_ICONS.length],
                }));
                setAvailableMaterials(mats);
            }

            if (prodRes.status) {
                const p = prodRes.response.data;
                const mList = matRes.response?.data || [];

                // Convert existing images to File objects so they can be re-uploaded to satisfy multipart/form-data requirements
                const productImageFiles = (await Promise.all((p.productImage || []).map((img) => urlToFile(`${IMAGE_BASE_URL}/${img.docPath}/${img.docName}`, img.originalName || img.docName, "image/png")))).filter(f => f);
                const additionalImageFiles = (await Promise.all((p.additionalImage || []).map((img) => urlToFile(`${IMAGE_BASE_URL}/${img.docPath}/${img.docName}`, img.originalName || img.docName, "image/png")))).filter(f => f);

                const updatedProduct = {
                    ...p,
                    productImage: productImageFiles,
                    additionalImage: additionalImageFiles
                };
                setProduct(updatedProduct);

                const initialMap = {};
                const activeMats = new Set();

                (p.customerAttribute || []).forEach(row => {
                    const rowId = row._id;
                    (row.materials || []).forEach(mInfo => {
                        // Find the material in the active material list by ID or Name
                        const matched = mList.find(m => m._id === mInfo.material || m.name === mInfo.material);

                        // If the material is not in the active list, skip it as requested
                        if (!matched) return;

                        const mId = matched._id;
                        activeMats.add(mId);

                        if (!initialMap[mId]) initialMap[mId] = {};
                        console.log(initialMap[mId], "initialMap[mId]");

                        const pricing = {};
                        (mInfo.pricing || []).forEach(pr => {
                            pricing[pr.currencyId] = pr.price ?? "";
                        });

                        initialMap[mId][rowId] = {
                            selected: true,
                            stock: mInfo.stock ?? "",
                            sku: mInfo.sku || "",
                            pricing: pricing
                        };
                    });
                });

                setMaterialPricing(initialMap);
                console.log(activeMats, "activeMats");
                setActiveMaterials(Array.from(activeMats));
                if (activeMats.size > 0) setActiveTab(Array.from(activeMats)[0]);
            }
            if (currRes.status) setCurrencies(currRes.response.data);
        } catch (e) {
            ShowNotifications.showAlertNotification("Failed to load data", false);
        } finally {
            setLoading(false);
        }
    };

    const allSizes = useMemo(() => {
        if (!product) return [];
        return (product.customerAttribute || []).map(s => ({
            ...s,
            id: s._id,
            displayStandard: s.standard === "AS568A" ? "AS 568A" : "JIS B 2401"
        }));
    }, [product]);
    console.log(allSizes, "allSizes");

    const toggleMaterial = (mId) => {
        if (activeMaterials.includes(mId)) {
            const next = activeMaterials.filter(x => x !== mId);
            setActiveMaterials(next);
            setActiveTab(next[0] || null);
        } else {
            setActiveMaterials(prev => [...prev, mId]);
            setActiveTab(mId);

            // Initialize material state if not exists
            setMaterialPricing(prev => {
                if (prev[mId]) return prev;
                const newEntries = {};
                const mName = availableMaterials.find(m => m.id === mId)?.label || "";
                allSizes.forEach(size => {
                    newEntries[size.id] = {
                        selected: false,
                        stock: "",
                        sku: (size.baseSku && mName) ? `${size.baseSku}-${mName}` : "",
                        pricing: {}
                    };
                });
                return { ...prev, [mId]: newEntries };
            });
        }
    };

    const handlePriceChange = (mId, sizeId, currencyId, value) => {
        // Enforce non-negative value
        const numericValue = value === "" ? "" : Math.max(0, parseFloat(value) || 0).toString();

        setMaterialPricing(prev => ({
            ...prev,
            [mId]: {
                ...(prev[mId] || {}),
                [sizeId]: {
                    ...(prev[mId]?.[sizeId] || {}),
                    pricing: {
                        ...(prev[mId]?.[sizeId]?.pricing || {}),
                        [currencyId]: numericValue
                    }
                }
            }
        }));
    };

    const handleStockChange = (mId, sizeId, value) => {
        // Enforce non-negative integer
        const numericValue = value === "" ? "" : Math.max(0, parseInt(value) || 0).toString();

        setMaterialPricing(prev => ({
            ...prev,
            [mId]: {
                ...(prev[mId] || {}),
                [sizeId]: {
                    ...(prev[mId]?.[sizeId] || {}),
                    stock: numericValue
                }
            }
        }));
    };

    const handleSkuChange = (mId, sizeId, value) => {
        setMaterialPricing(prev => ({
            ...prev,
            [mId]: {
                ...(prev[mId] || {}),
                [sizeId]: {
                    ...(prev[mId]?.[sizeId] || {}),
                    sku: value
                }
            }
        }));
    };

    const toggleVariant = (mId, sizeId) => {
        setMaterialPricing(prev => {
            const matData = prev[mId] || {};
            const rowData = matData[sizeId] || { selected: false, stock: "", sku: "", pricing: {} };
            const isSelected = !rowData.selected;

            const nextRow = { ...rowData, selected: isSelected };

            if (isSelected && !nextRow.sku) {
                const size = allSizes.find(s => s.id === sizeId);
                const mName = availableMaterials.find(m => m.id === mId)?.label;
                if (size && size.baseSku && mName) {
                    nextRow.sku = `${size.baseSku}-${mName}`;
                }
            }

            if (!isSelected) {
                setFieldErrors(epr => ({
                    ...epr,
                    [mId]: { ...(epr[mId] || {}), [sizeId]: null }
                }));
            }

            return {
                ...prev,
                [mId]: { ...matData, [sizeId]: nextRow }
            };
        });
    };

    const toggleAll = (mId) => {
        if (!mId) return;
        const currentMatData = materialPricing[mId] || {};

        const anyUnselected = allSizes.some(size => {
            const row = currentMatData[size.id];
            return !row || !row.selected;
        });

        const newState = anyUnselected;

        setMaterialPricing(prev => {
            const matData = prev[mId] || {};
            const nextMatData = { ...matData };
            const mName = availableMaterials.find(m => m.id === mId)?.label;

            allSizes.forEach(size => {
                const rowData = matData[size.id] || {
                    selected: false, stock: "", sku: "", pricing: {}
                };
                const nextRow = { ...rowData, selected: newState };
                if (newState && !nextRow.sku && size.baseSku && mName) {
                    nextRow.sku = `${size.baseSku}-${mName}`;
                }
                nextMatData[size.id] = nextRow;
            });
            // test

            return { ...prev, [mId]: nextMatData };
        });

        if (!newState) {
            setFieldErrors(epr => ({ ...epr, [mId]: null }));
        }
    };

    const Validate = () => {
        const errors = {};
        let hasError = false;

        activeMaterials.forEach(mId => {
            const mData = materialPricing[mId] || {};
            allSizes.forEach(size => {
                const rowId = size.id;
                const rowData = mData[rowId] || {};

                if (rowData.selected) {
                    const rowErrors = {};
                    const { stock, sku, pricing } = rowData;

                    // if (!sku || sku.trim() === "") rowErrors.sku = "SKU is required";
                    if (stock === "" || stock === undefined) rowErrors.stock = "Stock required";

                    currencies.forEach(curr => {
                        const p = (pricing || {})[curr._id];
                        if (p === "" || p === undefined || isNaN(Number(p))) {
                            rowErrors[curr._id] = "Price required";
                        }
                    });

                    if (Object.keys(rowErrors).length > 0) {
                        if (!errors[mId]) errors[mId] = {};
                        errors[mId][rowId] = rowErrors;
                        hasError = true;
                    }
                }
            });
        });

        setFieldErrors(errors);
        return !hasError;
    };

    const handleSave = async () => {
        if (activeMaterials.length === 0) {
            ShowNotifications.showAlertNotification("Please select at least one material", false);
            return;
        }
        if (!Validate()) {
            ShowNotifications.showAlertNotification("Please fill all the required fields in the table", false);
            return;
        }
        setSaving(true);
        try {
            const updatedCustomerAttribute = (product.customerAttribute || []).map(row => {
                const rowId = row._id;
                const materials = activeMaterials
                    .filter(mId => materialPricing[mId]?.[rowId]?.selected)
                    .map(mId => {
                        const rowData = materialPricing[mId][rowId];
                        const { pricing, stock, sku } = rowData;

                        // Find existing material to preserve its _id
                        const existingMaterial = (row.materials || []).find(
                            m => m.material === mId ||
                                m.material?._id === mId ||
                                m.material?.toString() === mId
                        );

                        return {
                            _id: existingMaterial?._id,
                            material: mId,
                            sku: sku,
                            stock: Number(stock) || 0,
                            pricing: Object.entries(pricing || {}).map(([cId, price]) => ({
                                currencyId: cId,
                                price: Number(price) || 0
                            }))
                        };
                    });
                return { ...row, materials };
            });

            const fd = new FormData();
            // Basic required fields
            fd.append("categoryId", product.categoryId || "");
            fd.append("subCategory", product.subCategory || "");
            fd.append("productName", product.productName || "");
            fd.append("brand", product.brand || "");
            fd.append("slug", product.slug || "");
            fd.append("hsn", product.hsn || "");

            // Additional fields to match full product update expectation
            if (product.tax) fd.append("tax", product.tax || "");
            if (product.tagAndLabel) fd.append("tagAndLabel", product.tagAndLabel || "");

            fd.append("lowStockAlert", String(product.lowStockAlert === true));
            fd.append("lowStockQuantity", String(Number(product.lowStockQuantity) || 0));
            fd.append("quantityPerPack", String(product.quantityPerPack || 1));
            if (product.packingType) fd.append("packingType", product.packingType || "");

            fd.append("refundable", String(product.refundable === true));
            fd.append("productStatus", String(product.productStatus === true || product.productStatus === "active" || product.productStatus === "true"));
            if (product.description) fd.append("description", product.description || "");

            // Discount and Tax fields
            fd.append("applicableForCustomer", String(product.applicableForCustomer === true || product.applicableForCustomer === "true"));
            fd.append("customerDiscount", String(Number(product.customerDiscount) || 0));
            fd.append("customerTax", String(Number(product.customerTax) || 0));
            fd.append("applicableForWholesale", String(product.applicableForWholesale === true || product.applicableForWholesale === "true"));
            fd.append("wholesalerDiscount", String(Number(product.wholesalerDiscount) || 0));
            fd.append("wholesalerTax", String(Number(product.wholesalerTax) || 0));

            // Meta tags
            if (product.metaTitle) fd.append("metaTitle", product.metaTitle || "");
            if (product.metaKeyword) fd.append("metaKeyword", product.metaKeyword || "");
            if (product.metaDesc) fd.append("metaDesc", product.metaDesc || "");
            if (product.delivery) fd.append("delivery", product.delivery || "");
            // Image handling (keeping existing images)
            (product.productImage || []).forEach((img) => { if (img instanceof File) fd.append("productImage", img); });
            (product.additionalImage || []).forEach((img) => { if (img instanceof File) fd.append("additionalImages", img); });

            // Material Pricing Data
            fd.append("customerAttribute", JSON.stringify(updatedCustomerAttribute));

            console.log("Saving Payload:", Object.fromEntries(fd.entries()))

            const res = await productApi.updateproduct(fd, product._id);
            if (res.status) {
                setTimeout(() => {
                    navigate('/product');
                }, 1000);
            }
        } catch (e) {
            ShowNotifications.showAlertNotification("Failed to save changes", false);
        } finally {
            setSaving(false);
        }
    };

    /* ── Loading ── */
    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: '#0f1623' }}>
            <div style={{ textAlign: 'center' }}>
                <div className="mpl-spinner" />
                <p style={{ color: '#64748b', fontSize: '12px', letterSpacing: '2px', marginTop: '16px', fontFamily: 'var(--mpl-mono)' }}>LOADING PRODUCT DATA</p>
            </div>
        </div>
    );
    if (!product) return <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>Product not found.</div>;

    const activeMat = availableMaterials.find(m => m.id === activeTab);

    return (
        <div >

            {/* ── Page Header ── */}
            <div className="mpl-header">
                <div className="mpl-header-left">
                    <div>
                        <h6 className="mpl-title">{product.name || product.productName || 'Material Pricing'}</h6>
                        <div className="mpl-meta">
                            <span className="mpl-chip">{allSizes.length} Size Variants</span>
                            <span className="mpl-chip">{currencies.length} Currencies</span>
                            <span className="mpl-chip">{activeMaterials.length} Active Materials</span>
                        </div>
                    </div>
                </div>
                <div className="mpl-header-actions">
                    <button className="mpl-btn-ghost" onClick={() => navigate('/product')}>Discard</button>
                    <button
                        className="mpl-btn-primary"
                        onClick={handleSave}
                        disabled={saving || activeMaterials.length === 0}
                    >
                        {saving
                            ? <><span className="mpl-btn-spinner" /> Saving…</>
                            : <><Icon icon="solar:check-circle-bold" width="16" /> Save All Pricing</>
                        }
                    </button>
                </div>
            </div>

            <div className="mpl-body">

                {/* ── Material Selector ── */}
                <section className="mpl-section">
                    <div className="mpl-section-label">
                        <Icon icon="solar:layers-minimalistic-bold-duotone" width="14" />
                        SELECT MATERIALS
                    </div>
                    <div className="mpl-material-grid">
                        {availableMaterials.map(m => {
                            const active = activeMaterials.includes(m.id);
                            const focused = activeTab === m.id;
                            return (
                                <button
                                    key={m.id}
                                    className={`mpl-mat-card ${active ? 'active' : ''} ${focused ? 'focused' : ''}`}
                                    onClick={() => active ? (setActiveTab(m.id)) : toggleMaterial(m.id)}
                                    onDoubleClick={() => toggleMaterial(m.id)}
                                    style={{ '--accent': "#1a2238" }}
                                    title={active ? 'Click to focus · Double-click to remove' : 'Click to add'}
                                >
                                    <div className="mpl-mat-icon-wrap">
                                        <Icon icon={m.icon} width="22" style={{ color: "#1a2238" }} />
                                    </div>
                                    <div className="mpl-mat-text">
                                        <span className="mpl-mat-name">{m.label}</span>
                                    </div>
                                    <div className={`mpl-mat-check ${active ? 'on' : ''}`}>
                                        {active
                                            ? <Icon icon="solar:check-circle-bold" width="16" style={{ color: "#1a2238" }} />
                                            : <Icon icon="solar:add-circle-line-duotone" width="16" style={{ color: '#334155' }} />
                                        }
                                    </div>
                                    {active && <div className="mpl-mat-bar" style={{ background: "#1a2238" }} />}
                                </button>
                            );
                        })}
                    </div>
                    {activeMaterials.length > 0 && (
                        <div className="mpl-tab-row">
                            {activeMaterials.map(mId => {
                                const m = availableMaterials.find(x => x.id === mId);
                                return (
                                    <button
                                        key={mId}
                                        className={`mpl-tab ${activeTab === mId ? 'active' : ''}`}
                                        onClick={() => setActiveTab(mId)}
                                        style={{ '--accent': "#1a2238" }}
                                    >
                                        <span className="mpl-tab-dot" style={{ background: "#f6993f" }} />
                                        {m.label}
                                        <span
                                            className="mpl-tab-close"
                                            onClick={e => { e.stopPropagation(); toggleMaterial(mId); }}
                                        >×</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* ── Pricing Table Panel ── */}
                {activeMaterials.length === 0 ? (
                    <div className="mpl-empty">
                        <Icon icon="solar:layers-minimalistic-bold-duotone" width="48" style={{ color: '#1e3a5f', marginBottom: '16px' }} />
                        <p className="mpl-empty-title">No materials selected</p>
                        <p className="mpl-empty-sub">Choose a material above to configure pricing and stock</p>
                    </div>
                ) : activeTab && (
                    <section className="mpl-section mpl-table-section">
                        <div className="mpl-table-header">
                            <div className="mpl-table-title-block">
                                <Icon icon={activeMat?.icon} width="18" style={{ color: "#1a2238" }} />
                                <div>
                                    <div className="mpl-table-title">{activeMat?.label} — Pricing & Stock</div>
                                    <div className="mpl-table-subtitle">{allSizes.length} variants across {currencies.length} currencies</div>
                                </div>
                            </div>
                        </div>

                        <div className="mpl-table-wrap">
                            <div className="mpl-tbody-wrap">
                                <table className="mpl-table mpl-table-fixed">
                                    <thead className="mpl-thead-wrap">
                                        <tr>
                                            <th className="mpl-th-sno" style={{ width: "50px", textAlign: "center" }}>S.No</th>
                                            <th className="mpl-th-check">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <input
                                                        type="checkbox"
                                                        className="mpl-checkbox"
                                                        checked={allSizes.length > 0 && allSizes.every(size => !!materialPricing[activeTab]?.[size.id]?.selected)}
                                                        onChange={() => toggleAll(activeTab)}
                                                    />
                                                    <span>Select</span>
                                                </div>
                                            </th>
                                            <th className="mpl-th-std">Standard</th>
                                            <th className="mpl-th-size">Size / Specification</th>
                                            <th className="mpl-th-stock">
                                                <div className="mpl-th-inner">
                                                    <Icon icon="solar:box-bold-duotone" width="12" />
                                                    Stock
                                                </div>
                                            </th>
                                            {/* <th className="mpl-th-sku">
                                                 <div className="mpl-th-inner">
                                                     <Icon icon="solar:tag-bold-duotone" width="12" />
                                                     SKU
                                                 </div>
                                             </th> */}
                                            {currencies.map(c => (
                                                <th key={c._id} className="mpl-th-price">
                                                    <div className="mpl-th-inner">
                                                        <span className="mpl-currency-sym">{c.symbol}</span>
                                                        {c.code}
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allSizes.map((size, idx) => {
                                            const rowData = materialPricing[activeTab]?.[size.id] || {};
                                            const isSelected = !!rowData.selected;
                                            const rowErrors = fieldErrors[activeTab]?.[size.id] || {};

                                            return (
                                                <tr key={`${activeTab}-${size.id}-${idx}`} className={`mpl-tr ${isSelected ? 'active' : 'disabled'}`}>
                                                    <td className="mpl-td-sno text-center" style={{ fontSize: "11px", color: "#64748b" }}>{idx + 1}</td>
                                                    <td className="mpl-td-check">
                                                        <input
                                                            type="checkbox"
                                                            className="mpl-checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleVariant(activeTab, size.id)}
                                                        />
                                                    </td>
                                                    <td className="mpl-td-std" style={{ textAlign: "right" }}>
                                                        <span className={`mpl-std-badge ${size.standard === "AS568A" ? 'as' : 'jis'}`}>
                                                            {size.displayStandard}
                                                        </span>
                                                    </td>
                                                    <td className="mpl-td-size">
                                                        <span className="mpl-size-code">{size.sizeCode}</span>
                                                        <span className="mpl-size-meta">
                                                            ID {size.metric_id_mm}mm &nbsp;·&nbsp; CS {size.metric_cs_mm}mm
                                                        </span>
                                                    </td>
                                                    <td className="mpl-td-input">
                                                        <div className="mpl-input-group">
                                                            <input
                                                                type="number"
                                                                className={`mpl-input stock-input ${rowErrors.stock ? 'error' : ''}`}
                                                                placeholder="0"
                                                                min="0"
                                                                disabled={!isSelected}
                                                                value={rowData.stock ?? ''}
                                                                onKeyDown={(e) => {
                                                                    if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                onChange={e => handleStockChange(activeTab, size.id, e.target.value)}
                                                            />
                                                            {isSelected && rowErrors.stock && <div className="mpl-field-error">{rowErrors.stock}</div>}
                                                        </div>
                                                    </td>
                                                    {/* <td className="mpl-td-input">
                                                         <div className="mpl-input-group">
                                                             <input
                                                                 type="text"
                                                                 className={`mpl-input sku-input ${rowErrors.sku ? 'error' : ''}`}
                                                                 placeholder="SKU"
                                                                 disabled={!isSelected}
                                                                 value={rowData.sku || ''}
                                                                 onChange={e => handleSkuChange(activeTab, size.id, e.target.value)}
                                                             />
                                                             {isSelected && rowErrors.sku && <div className="mpl-field-error">{rowErrors.sku}</div>}
                                                         </div>
                                                     </td> */}
                                                    {currencies.map(c => (
                                                        <td key={c._id} className="mpl-td-input">
                                                            <div className="mpl-input-group">
                                                                <div className="mpl-price-wrap">
                                                                    <span className="mpl-price-sym">{c.symbol}</span>
                                                                    <input
                                                                        type="number"
                                                                        className={`mpl-input ${rowErrors[c._id] ? 'error' : ''}`}
                                                                        placeholder="0.00"
                                                                        min="0"
                                                                        disabled={!isSelected}
                                                                        value={rowData.pricing?.[c._id] ?? ''}
                                                                        onKeyDown={(e) => {
                                                                            if (['e', 'E', '+', '-'].includes(e.key)) {
                                                                                e.preventDefault();
                                                                            }
                                                                        }}
                                                                        onChange={e => handlePriceChange(activeTab, size.id, c._id, e.target.value)}
                                                                    />
                                                                </div>
                                                                {isSelected && rowErrors[c._id] && <div className="mpl-field-error">{rowErrors[c._id]}</div>}
                                                            </div>
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </section>
                )}
            </div>

            {/* ════ Styles ════ */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=DM+Sans:wght@300;400;500;600;700&display=swap');

                :root {
                    --primary: #1a2238;
                    --navy-950: #060d1a;
                    --navy-900: #0a1628;
                    --navy-800: #1a2238; /* Project Primary Dark Blue */
                    --navy-750: #232c44;
                    --navy-700: #2d3748;
                    --navy-600: #1e3a5f;
                    --navy-500: #234a78;
                    --navy-400: #2d6098;
                    --slate-700: #2d3748;
                    --slate-600: #4a5568;
                    --slate-500: #718096;
                    --slate-400: #a0aec0;
                    --slate-300: #cbd5e0;
                    --slate-200: #e2e8f0;
                    --slate-100: #edf2f7;
                    --slate-50:  #f7fafc;
                }

                .mpl-root {
                    font-family: var(--mpl-sans);
                    background: #f8fafc;
                    min-height: 100vh;
                    color: #1e293b;
                    padding-bottom: 60px;
                }

                /* ── Header ── */
                .mpl-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    padding: 28px 36px 24px;
                    background: #1a2238;
                    color: #fff;
                    gap: 24px;
                    flex-wrap: wrap;
                }
                .mpl-header-left { display:flex; align-items:flex-start; gap:16px; }
                .mpl-back-btn {
                    width: 36px; height: 36px;
                    background: var(--navy-700);
                    border: 1px solid var(--navy-500);
                    border-radius: 8px;
                    color: var(--slate-300);
                    display: flex; align-items:center; justify-content:center;
                    cursor: pointer;
                    margin-top: 4px;
                    flex-shrink: 0;
                    transition: all .15s;
                }
                .mpl-back-btn:hover { background: var(--navy-600); color: #fff; }
                .mpl-breadcrumb { font-size: 11px; color: var(--text-muted); letter-spacing: 0.5px; margin-bottom: 4px; font-family: var(--mpl-mono); }
                .mpl-title { font-size: 22px; font-weight: 700; color: #fff; margin: 0 0 8px; letter-spacing: -0.3px; }
                .mpl-meta { display:flex; gap:8px; flex-wrap:wrap; }
                .mpl-chip {
                    font-size: 10px; font-family: var(--mpl-mono);
                    background: var(--navy-700); color: var(--slate-300);
                    border: 1px solid var(--navy-500);
                    padding: 3px 10px; border-radius: 20px;
                    letter-spacing: 0.5px;
                }
                .mpl-header-actions { display:flex; gap:10px; align-items:center; flex-shrink:0; padding-top:4px; }
                .mpl-btn-ghost {
                    background: #6c757d;
                    border: 1px solid #6c757d;
                    color: #fff;
                    font-family: var(--mpl-sans);
                    font-size: 13px; font-weight: 600;
                    padding: 9px 22px; border-radius: 8px;
                    cursor: pointer; transition: all .15s;
                }
                .mpl-btn-ghost:hover { background: #5a6268; border-color: #5a6268; box-shadow: 0 4px 12px rgba(0,0,0,.15); }
                .mpl-btn-primary {
                    background: #f6993f;
                    border: 1px solid #f6993f;
                    color: #fff;
                    font-family: var(--mpl-sans);
                    font-size: 13px; font-weight: 700;
                    padding: 10px 24px; border-radius: 8px;
                    cursor: pointer; transition: all .15s;
                    display: flex; align-items:center; gap:8px;
                    letter-spacing: 0.2px;
                }
                .mpl-btn-primary:hover:not(:disabled) { background: #e68a2e; border-color: #e68a2e; box-shadow: 0 4px 12px rgba(246,153,63,.3); }
                .mpl-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
                .mpl-btn-spinner {
                    width: 14px; height: 14px;
                    border: 2px solid rgba(26,34,56,.3);
                    border-top-color: #1a2238;
                    border-radius: 50%;
                    animation: spin .7s linear infinite;
                }

                /* ── Body ── */
                .mpl-body { padding: 28px 36px; display:flex; flex-direction:column; gap:24px; }
                .mpl-section { /* container */ }
                .mpl-section-label {
                    font-family: var(--mpl-mono);
                    font-size: 10px; letter-spacing: 1.5px;
                    color: var(--text-muted);
                    display: flex; align-items:center; gap:7px;
                    margin-bottom: 14px;
                    text-transform: uppercase;
                }

                /* ── Material Cards ── */
                .mpl-material-grid {
                    display: flex;
                    overflow-x: auto;
                    gap: 12px;
                    padding: 4px 4px 12px;
                    scroll-behavior: smooth;
                    -webkit-overflow-scrolling: touch;
                }
                .mpl-material-grid::-webkit-scrollbar { height: 6px; }
                .mpl-material-grid::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
                .mpl-material-grid::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .mpl-material-grid::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .mpl-mat-card {
                    position: relative;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 16px;
                    display: flex; align-items:center; gap:12px;
                    cursor: pointer;
                    text-align: left;
                    transition: all .15s ease;
                    flex: 0 0 210px; /* Fixed width for horizontal scroll */
                }
                .mpl-mat-card:hover {
                    border-color: var(--accent, #38bdf8);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }
                .mpl-mat-card.active {
                    background: #fff;
                    border-color: var(--accent, #38bdf8);
                    box-shadow: 0 0 0 1px var(--accent, #38bdf8), 0 4px 12px rgba(0,0,0,0.05);
                }
                .mpl-mat-icon-wrap {
                    width: 36px; height: 36px;
                    background: #f1f5f9;
                    border-radius: 8px;
                    display: flex; align-items:center; justify-content:center;
                    flex-shrink: 0;
                }
                .mpl-mat-text { flex:1; min-width:0; }
                .mpl-mat-name { display:block; font-size:13px; font-weight:600; color: #1e293b; }
                .mpl-mat-sub  { display:block; font-size:10px; color: #64748b; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
                .mpl-mat-check { flex-shrink:0; }

                /* ── Tab row ── */
                .mpl-tab-row {
                    display: flex; gap:4px; flex-wrap:wrap;
                    margin-top: 24px;
                    padding: 5px;
                    background: #1a2238;
                    border-radius: 12px;
                    border: 1px solid rgba(255,255,255,0.08);
                    width: fit-content;
                }
                .mpl-tab {
                    display: flex; align-items:center; gap:7px;
                    padding: 7px 14px;
                    border-radius: 7px;
                    border: none;
                    background: transparent;
                    color: var(--slate-400);
                    font-size: 12px; font-weight: 600;
                    font-family: var(--mpl-sans);
                    cursor: pointer;
                    transition: all .15s;
                    letter-spacing: 0.2px;
                }
                .mpl-tab:hover { background: var(--navy-800); color: var(--slate-200); }
                .mpl-tab.active {
                    background: rgba(255,255,255,0.08);
                    color: #fff;
                }
                .mpl-tab-dot { width:6px; height:6px; border-radius:50%; flex-shrink:0; }
                .mpl-tab-close {
                    width: 16px; height: 16px;
                    border-radius: 50%;
                    background: var(--navy-600);
                    display: inline-flex; align-items:center; justify-content:center;
                    font-size: 11px;
                    color: var(--slate-400);
                    line-height: 1;
                    margin-left: 2px;
                    transition: all .12s;
                }
                .mpl-tab-close:hover { background: #7f1d1d; color: #fca5a5; }

                /* ── Table Section ── */
                .mpl-table-section {}
                .mpl-table-header {
                    display: flex; align-items:center; justify-content:space-between;
                    margin-bottom: 14px; flex-wrap: wrap; gap: 12px;
                }
                .mpl-hint { font-size: 11px; color: #64748b; font-style: italic; }
                .mpl-td-check { width: 90px; text-align: center; }
                .mpl-th-check { width: 90px; font-size: 10px; color: #64748b; text-align: center; }
                .mpl-checkbox {
                    width: 16px; height: 16px; cursor: pointer;
                    accent-color: #1a2238; vertical-align: middle;
                }
                .mpl-tr.disabled { opacity: 0.6; background: #f8fafc; }
                .mpl-input:disabled { background: #f1f5f9; cursor: not-allowed; border-color: #e2e8f0; }
                .mpl-input.error { border-color: #f87171 !important; }
                .mpl-input-group { position: relative; display: flex; flex-direction: column; gap: 4px; }
                .mpl-field-error {
                    font-size: 9px; color: #ef4444; font-weight: 500;
                    margin-top: 2px; line-height: 1;
                }
                .mpl-table-title-block { display:flex; align-items:center; gap:12px; }
                .mpl-table-title { font-size:15px; font-weight:600; color: var(--text-primary); }
                .mpl-table-subtitle { font-size:11px; color: var(--text-muted); margin-top:2px; font-family: var(--mpl-mono); }
                .mpl-table-legend { display:flex; gap:16px; flex-wrap:wrap; }
                .mpl-legend-item { display:flex; align-items:center; gap:5px; font-size:11px; color: var(--text-muted); font-family:var(--mpl-mono); }
                .mpl-legend-dot { width:6px; height:6px; border-radius:50%; }
                .mpl-legend-dot.stock { background:#fbbf24; }
                .mpl-legend-dot.price { background:#38bdf8; }

                .mpl-table-wrap {
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    overflow: hidden; /* for border radius */
                }

                .mpl-table-fixed {
                    width: 100%;
                    table-layout: fixed;
                    border-collapse: collapse;
                }

                /* Fixed Header */
                .mpl-thead-wrap {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    background: #f8fafc;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .mpl-table-fixed thead th {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    background: #f8fafc;
                    box-shadow: inset 0 -1px 0 #e2e8f0;
                }

                /* Scrollable Body */
                .mpl-tbody-wrap {
                    max-height: 480px;
                    overflow-y: auto;
                    overflow-x: auto;
                }

                /* Custom Scrollbar */
                .mpl-tbody-wrap::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                }
                .mpl-tbody-wrap::-webkit-scrollbar-track {
                    background: #f1f5f9;
                }
                .mpl-tbody-wrap::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 3px;
                }
                .mpl-tbody-wrap::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }

                .mpl-table {
                    width: 100%; border-collapse: collapse;
                    font-family: var(--mpl-sans);
                }
                .mpl-table thead tr {
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                }
                .mpl-table th {
                    padding: 11px 14px;
                    font-size: 10px; font-weight: 600;
                    color: #64748b;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    font-family: var(--mpl-mono);
                    white-space: nowrap;
                }
                .mpl-th-inner { display:flex; align-items:center; gap:5px; justify-content:center; }
                .mpl-th-std   { width: 100px; text-align: right; }
                .mpl-th-size  { width: 220px; text-align: center; }
                .mpl-th-stock { width: 120px; text-align: center; }
                .mpl-th-sku   { width: 160px; text-align: center; }
                .mpl-th-price { width: 120px; text-align: center; }
                .mpl-currency-sym { color: var(--slate-300); font-size:11px; }

                .mpl-tr { border-bottom: 1px solid var(--line); transition: background .1s; }
                .mpl-tr:last-child { border-bottom: none; }
                .mpl-tr:hover { background: rgba(255,255,255,.02); }
                .mpl-table td { padding: 10px 14px; vertical-align: middle; }

                .mpl-td-std { text-align: right; padding-right: 10px; }
                .mpl-std-badge {
                    display: inline-block;
                    font-family: var(--mpl-mono);
                    font-size: 9px; font-weight: 600;
                    padding: 3px 8px; border-radius: 4px;
                    letter-spacing: 0.5px; text-transform: uppercase;
                }
                .mpl-std-badge.as  { background: rgba(56,189,248,.12); color: #38bdf8; border:1px solid rgba(56,189,248,.2); }
                .mpl-std-badge.jis { background: rgba(52,211,153,.12); color: #34d399; border:1px solid rgba(52,211,153,.2); }

                .mpl-td-size { text-align: center; }
                .mpl-td-input { text-align: center; } 
                .mpl-size-code { display:block; font-size:15px; font-weight:700; color: #1e293b; font-family:var(--mpl-mono); }
                .mpl-size-meta { display:block; font-size:11px; color: #64748b; margin-top:4px; font-family:var(--mpl-mono); }

                .mpl-price-wrap,
                .mpl-input.stock-input,
                .mpl-input.sku-input {
                    display: inline-flex;
                    align-items: center;
                    position: relative;
                    width: 130px;
                    height: 32px;
                    background: #fff;
                    border: 1px solid #cbd5e1;
                    border-radius: 6px;
                    transition: all .1s;
                    overflow: hidden;
                }

                .mpl-price-wrap:focus-within,
                .mpl-input.stock-input:focus,
                .mpl-input.sku-input:focus {
                    box-shadow: 0 0 0 2px rgba(56,189,248,0.1);
                    outline: none;
                }
                
                .mpl-price-sym {
                    position: absolute;
                    left: 8px;
                    font-size: 11px;
                    font-weight: 700;
                    color: #64748b;
                    pointer-events: none;
                    z-index: 10;
                }

                .mpl-input {
                    width: 100%;
                    height: 100%;
                    background: transparent;
                    border: none;
                    outline: none;
                    text-align: right;
                    padding: 0 10px;
                    font-size: 12px;
                    font-weight: 600;
                    font-family: var(--mpl-mono);
                    color: #1e293b;
                }
                .mpl-price-wrap .mpl-input {
                    padding-left: 22px; 
                }
                .mpl-input.stock-input {
                    text-align: center;
                    padding: 0;
                }
                .mpl-input::placeholder { color: #cbd5e1; }
                input[type=number]::-webkit-inner-spin-button,
                input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; }

            
                .mpl-table-footer {
                    padding: 10px 16px;
                    background: var(--navy-900);
                    border-top: 1px solid var(--line);
                    font-size: 11px; color: var(--text-muted);
                    font-family: var(--mpl-mono);
                    border-radius: 0 0 12px 12px;
                }
                .mpl-table-footer strong { color: var(--slate-300); }

                /* ── Empty ── */
                .mpl-empty {
                    text-align: center; padding: 64px 20px;
                    border: 1px dashed var(--navy-600);
                    border-radius: 16px;
                    display: flex; flex-direction:column; align-items:center;
                }
                .mpl-empty-title { font-size:16px; font-weight:600; color: var(--slate-400); margin:0 0 6px; }
                .mpl-empty-sub   { font-size:13px; color: var(--text-muted); margin:0; }

                /* ── Spinner ── */
                .mpl-spinner {
                    width: 36px; height: 36px;
                    border: 3px solid rgba(35,74,120,.3);
                    border-top-color: #234a78;
                    border-radius: 50%;
                    animation: spin .8s linear infinite;
                    margin: 0 auto;
                }
                @keyframes spin { to { transform: rotate(360deg); } }

                /* ── Responsive ── */
                @media (max-width: 768px) {
                    .mpl-header { padding: 20px 20px 16px; }
                    .mpl-body   { padding: 20px; }
                    .mpl-title  { font-size: 18px; }
                    .mpl-material-grid { padding-bottom: 8px; }
                    .mpl-header-actions .mpl-btn-ghost { display:none; }
                }
            `}</style>
        </div>
    );
}